<template>
  <modal
    width="560px"
    bodyPadding="0"
    :title="t('Disable Two-Factor Authentication')"
    @close="close"
  >
    <div class="wrapper">
      <div class="wrapper-container">
        <div class="pb-10">
          <ui-icon size="42" :color="colors.green" icon="fas fa-unlock-alt" />
        </div>
        <span class="mr-1">{{ t('For security please enter your password.') }}</span>

        <ui-input
          v-model="password"
          name="password"
          :label="t('Password')"
          :placeholder="t('Password')"
          :type="showPassword ? 'text' : 'password'"
          :append-inner-icon="showPassword ? 'fas fa-lock-open-alt' : 'fas fa-lock-alt'"
          :error-messages="fieldErrors.password"
          @change="clearFieldError('password')"
          @keyup.enter="submit"
          @click:appendInner="() => (showPassword = !showPassword)"
        />
      </div>
    </div>

    <template #footer>
      <div class="d-flex justify-end xs12">
        <ui-button
          :text="t('Cancel')"
          icon="fas fa-times"
          :disabled="apiLoading"
          @click="close"
        />

        <ui-button
          class="ml-8"
          :text="t('Done')"
          icon="fas fa-check"
          button-style="accent"
          :loading="apiLoading"
          @click="submit"
        />
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useI18n, useColors } from '@/composables';
import { getUserFromApi } from '@/services/api/api-services/load-profile';
import { postUserTwoFactorDisableApiRequest } from '@/services/api/user-api';
import type { FieldErrors } from '@/services/models';
import parseApiErrors from '@/utils/parseApiErrors';

const emit = defineEmits<{ close: [] }>();

const { t } = useI18n();
const colors = useColors();

const apiLoading = ref(false);
const showPassword = ref(false);
const password = ref('');
const fieldErrors = ref<FieldErrors>({});

const clearFieldError = (field: keyof FieldErrors) => {
  const newValue = { ...fieldErrors.value };
  delete newValue[field];
  fieldErrors.value = newValue;
};

const close = () => {
  emit('close');
};

const submit = async () => {
  fieldErrors.value = {};
  apiLoading.value = true;
  try {
    await postUserTwoFactorDisableApiRequest({ password: password.value });
    await getUserFromApi();
    close();
  } catch (error: unknown) {
    fieldErrors.value = parseApiErrors({ error, nested: true });
  } finally {
    apiLoading.value = false;
  }
};
</script>

<style lang="scss" scoped>

.wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
}
.wrapper-container {
  padding: 24px;
}
</style>
